import type { MasteryMapDomain } from '../model/types/mastery-map/interfaces'
import type {
  MasteryStandardItem,
  MasteryStrandBalance,
  MasteryStrandDetail,
  MasteryStrandPoint,
  MasteryStrandSegment,
  MasteryTest,
  MasteryTestSnapshot,
} from '../model/types/mastery-map-dashboard/interfaces'
import type { MasterySkillStatus, MasteryStatusFilter } from '../model/types/mastery-map-dashboard/types'

import { getStrand, getStrandCodes } from './summary'

export const MAX_COMPARED_TESTS = 3

const byTestOrder = (tests: MasteryTest[]) => (a: number, b: number): number =>
  tests.findIndex(test => test.id === a) - tests.findIndex(test => test.id === b)

/** Baseline (or the unit's first test) and the latest test. */
export const getDefaultSelection = (tests: MasteryTest[]): number[] => {
  const first = tests.at(0)
  const latest = tests.at(-1)

  if (!first || !latest) return []

  return first === latest ? [first.id] : [first.id, latest.id]
}

/** Never empties the selection and never grows it past MAX_COMPARED_TESTS. */
export const toggleSelection = (selected: number[], testId: number, tests: MasteryTest[]): number[] => {
  const isSelected = selected.includes(testId)

  if (isSelected) return selected.length > 1 ? selected.filter(id => id !== testId) : selected
  if (selected.length >= MAX_COMPARED_TESTS) return selected

  return [...selected, testId].sort(byTestOrder(tests))
}

export const normalizeSelection = (values: number[], tests: MasteryTest[]): number[] => {
  const known = values.filter(id => tests.some(test => test.id === id)).sort(byTestOrder(tests))

  return known.length ? known.slice(0, MAX_COMPARED_TESTS) : getDefaultSelection(tests)
}

export const getSelectedSnapshots = (snapshots: MasteryTestSnapshot[], selected: number[]): MasteryTestSnapshot[] =>
  snapshots.filter(snapshot => selected.includes(snapshot.test.id))

export const getComparisonCaption = (selected: MasteryTestSnapshot[]): string => {
  const reference = selected.at(0)
  const latest = selected.at(-1)

  if (!reference || !latest) return ''
  if (reference === latest) return `Showing ${reference.test.label}`

  return `Comparing ${reference.test.label} → ${latest.test.label}`
}

const toSegments = (points: MasteryStrandPoint[]): MasteryStrandSegment[] => {
  const reference = points.at(0)

  if (!reference) return []

  const gains = points.slice(1).map((point, index) => ({
    testId: point.testId,
    isReference: false,
    value: point.value - Math.max(...points.slice(0, index + 1).map(entry => entry.value)),
  }))

  return [
    { testId: reference.testId, isReference: true, value: reference.value },
    ...gains.filter(segment => segment.value > 0),
  ]
}

export const getStrandBalances = (
  selected: MasteryTestSnapshot[],
  domains: MasteryMapDomain[],
): MasteryStrandBalance[] => {
  const latest = selected.at(-1)

  if (!latest) return []

  return getStrandCodes(latest, domains).map((code) => {
    const points = selected.map(snapshot => ({
      testId: snapshot.test.id,
      value: getStrand(snapshot, code, domains).percentage,
    }))
    const from = points.at(0)?.value ?? 0
    const to = points.at(-1)?.value ?? 0

    return {
      code,
      title: getStrand(latest, code, domains).title,
      from,
      to,
      gain: to - from,
      points,
      segments: toSegments(points),
    }
  })
}

/** The gain a single test added over the previous selected item, for the legend-hover view. */
export const getPointGain = (balance: MasteryStrandBalance, testId: number): { from: number, to: number, gain: number } | null => {
  const index = balance.points.findIndex(point => point.testId === testId)
  const current = balance.points.at(index)
  const previous = index > 0 ? balance.points.at(index - 1) : undefined

  if (index === -1 || !current) return null
  if (!previous) return { from: current.value, to: current.value, gain: 0 }

  return { from: previous.value, to: current.value, gain: current.value - previous.value }
}

const isMastered = (snapshot: MasteryTestSnapshot, code: string): boolean =>
  snapshot.skills.some(skill => skill.code === code && skill.status === 'mastered')

export const getStrandDetails = (
  selected: MasteryTestSnapshot[],
  domains: MasteryMapDomain[],
): MasteryStrandDetail[] => {
  const reference = selected.at(0)
  const latest = selected.at(-1)

  if (!reference || !latest) return []

  return getStrandCodes(latest, domains).map((code) => {
    const strand = getStrand(latest, code, domains)
    const referenceStrand = getStrand(reference, code, domains)
    const skills = latest.skills.filter(skill => skill.strand === code)

    const toItem = (skill: { code: string, label: string | null }): MasteryStandardItem => ({
      code: skill.code,
      label: skill.label,
      isNew: reference !== latest && isMastered(latest, skill.code) && !isMastered(reference, skill.code),
    })

    const groupOf = (status: MasterySkillStatus): MasteryStandardItem[] =>
      skills.filter(skill => skill.status === status).map(toItem)

    const groups: Record<MasterySkillStatus, MasteryStandardItem[]> = {
      'mastered': groupOf('mastered'),
      'developing': groupOf('developing'),
      'up-next': groupOf('up-next'),
    }

    return {
      code,
      title: strand.title,
      total: strand.total,
      mastered: strand.counts.mastered,
      gain: strand.counts.mastered - referenceStrand.counts.mastered,
      counts: strand.counts,
      groups,
    }
  })
}

export const getStrandCodesWithStatus = (details: MasteryStrandDetail[], status: MasteryStatusFilter): string[] =>
  status === 'all' ? [] : details.filter(detail => detail.groups[status].length > 0).map(detail => detail.code)
